import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";

const TagInput = ({
  name,
  value = [],
  onChange,
  label,
  placeholder = "Ajouter un élément...",
  className = "",
  inputClassName = "",
  tagClassName = "",
  // LS-687 — autosuggestion native (datalist) : la saisie libre reste toujours possible,
  // les suggestions ne font que proposer une complétion, jamais ne la forcent.
  suggestions = [],
}) => {
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  // Gérer le clic en dehors du composant
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        // Fermer l'input si on clique en dehors
        setInputValue("");
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleKeyDown = (e) => {
    if ((e.key === "Enter" || e.key === ",") && inputValue.trim()) {
      e.preventDefault();
      addTag(inputValue.trim());
    } else if (e.key === "Backspace" && !inputValue && value.length > 0) {
      // Supprimer le dernier tag avec Backspace
      removeTag(value[value.length - 1]);
    }
  };

  const addTag = (tag) => {
    if (tag && !value.includes(tag)) {
      const newTags = [...value, tag];
      onChange({ target: { name, value: newTags } });
    }
    setInputValue("");
  };

  const removeTag = (tagToRemove) => {
    const newTags = value.filter((tag) => tag !== tagToRemove);
    onChange({ target: { name, value: newTags } });
  };

  const handlePaste = (e) => {
    const pastedText = e.clipboardData.getData("text");
    if (pastedText.includes(",")) {
      e.preventDefault();
      const newTags = pastedText
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t && !value.includes(t));
      if (newTags.length > 0) {
        onChange({ target: { name, value: [...value, ...newTags] } });
      }
      setInputValue("");
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      addTag(inputValue.trim());
    }
  };

  return (
    <div className={`form-group ${className}`} ref={wrapperRef}>
      {label && <label className="form-label">{label}</label>}

      <div
        className="form-control d-flex flex-wrap align-items-center gap-1 p-2 mb-0"
        // LS-679 — le .form-control global (index.scss) fige la hauteur à 38px :
        // sans height auto, les tags qui passent à la ligne débordent du champ
        // et la saisie s'affiche par-dessus le champ suivant.
        style={{ minHeight: "38px", height: "auto", cursor: "text", gap: 5 }}
        onClick={() => inputRef.current?.focus()}
      >
        {/* Tags existants */}
        {value.map((tag, index) => (
          <div
            key={`${tag}-${index}`}
            className={` pl-3 bg-primary badge-pill font-weight-light text-white d-flex align-items-center ${tagClassName}`}
            style={{ fontSize: 12 }}
          >
            <span>{tag}</span>
            <button
              type="button"
              className="btn border-0 text-white ml-1 rounded-pill btn-sm"
              onClick={(e) => {
                e.stopPropagation();
                removeTag(tag);
              }}
              aria-label="Supprimer"
            >
              <i className="fa fa-times" style={{ fontSize: 8 }}></i>
            </button>
          </div>
        ))}

        {/* Champ de saisie */}
        <input
          ref={inputRef}
          type="text"
          className={`border-0 bg-transparent flex-grow-1 ${inputClassName}`}
          style={{ outline: "none", minWidth: "100px" }}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={handleBlur}
          placeholder={value.length === 0 ? placeholder : ""}
          list={suggestions.length > 0 ? `${name}-suggestions` : undefined}
        />
      </div>

      {suggestions.length > 0 && (
        <datalist id={`${name}-suggestions`}>
          {suggestions
            .filter((s) => !value.includes(s))
            .map((s) => (
              <option key={s} value={s} />
            ))}
        </datalist>
      )}

      <small className="text-sm pl-2">
        Appuyez sur Entrée ou tapez une virgule pour ajouter
      </small>
    </div>
  );
};

TagInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  tagClassName: PropTypes.string,
  suggestions: PropTypes.arrayOf(PropTypes.string),
};

export default TagInput;
